import { Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference, Query } from '@angular/fire/compat/firestore';
import { QueryParam } from '../../models/query-parameter.model';
import { map, Observable, firstValueFrom } from 'rxjs';
import { OrderBy } from '../../models/order-by.model';
import { LoggerService } from '../logger.service';
import { Quiz } from '../../../../../../models/Quiz';

export const collection = {
  users: 'Users',
  quizzes: 'Quizzes',
  quizpackages: 'QuizPackages',
  liveassignments: 'LiveAssignments',
  liveassignmentparticipants: 'LiveAssignmentParticipants',
  quizresponses: 'QuizResponses',
  groups: 'Groups',
  groupMemberships: 'GroupMemberships',
  subjects: 'Subjects',
  lessons: 'Lessons',
  generationProcesses: 'GenerationProcesses',
  fields: 'Fields',
  topics: 'Topics',
  subtopics: 'Subtopics',
  studentSubjectStatistics: 'StudentSubjectStatistics',
  selfAssignments: 'SelfAssignments',
  neptunExports: 'NeptunExports',
  allowedEmailsWithoutNeptun: 'AllowedEmailsWithoutNeptun',
  externalLinks: 'ExternalLinks',
  earnedBadges: 'EarnedBadges',
  challengeSessions: 'ChallengeSessions',
  systemLog: 'SystemLog',
  knowledgeElement: 'KnowledgeElements',
  mail: 'mail',
  learningtasks: 'LearningTasks',
  classworkSessions: 'ClassworkSessions'
};

const archiveAwareCollections = new Set<string>([
  collection.users,
  collection.quizzes,
  collection.quizpackages,
  collection.quizresponses,
  collection.groups,
  collection.groupMemberships,
  collection.subjects,
  collection.lessons,
  collection.generationProcesses,
  collection.fields,
  collection.topics,
  collection.subtopics,
  collection.knowledgeElement,
  collection.learningtasks,
  collection.classworkSessions,
]);

@Injectable({
  providedIn: 'root',
})
export class FBaseService {
  constructor(
    private afs: AngularFirestore,
    private logger: LoggerService
  ) { }

  private filterArchivedDocument<T>(item: T | undefined): T | null {
    return (item as any)?.archived === true ? null : item ?? null;
  }

  private hasArchivedCondition(where?: Array<{ fieldPath?: string; field?: string }>): boolean {
    return !!where?.some(w => w?.fieldPath === 'archived' || w?.field === 'archived');
  }

  private shouldApplyActiveQueryDefault(collectionName: string, where?: Array<{ fieldPath?: string; field?: string }>): boolean {
    return archiveAwareCollections.has(collectionName) && !this.hasArchivedCondition(where);
  }

  private buildArchiveUpdate() {
    return {
      archived: true,
      'metadata.lastUpdatedAt': new Date().toISOString(),
      'metadata.lastUpdatedBy.id': this.logger.getUserId()
    };
  }

  private applyActiveDefault(collectionName: string, data: any, partialUpdate = false): any {
    if (!partialUpdate && archiveAwareCollections.has(collectionName) && data?.archived === undefined) {
      data.archived = false;
    }
    return data;
  }

  getDataByField(collection: string, where: { fieldPath: string; value: any; opStr: any }[] = [], orderBy?: OrderBy, limit?: number, startAfter?: any, noOrder: boolean = false): Observable<any[]> {
    return this.afs
      .collection(collection, ref => {
        let query: CollectionReference | Query = ref;
        if (this.shouldApplyActiveQueryDefault(collection, where)) {
          query = query.where('archived', '==', false);
        }
        for (const w of where) {
          if (w?.value !== undefined) {
            query = query.where(w.fieldPath, w.opStr, w.value);
          }
        }
        if (orderBy && orderBy.active && orderBy.direction) {
          query = query.orderBy(orderBy.active, orderBy.direction);
        } else {
          if (!noOrder) {
            query = query.orderBy('metadata.createdAt', 'desc');
          }
        }
        if (startAfter) {
          query = query.startAfter(startAfter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query;
      })
      .valueChanges();
  }

  get(
    collectionName: string,
    joinCode?: string,
    status?: string | null,
    except?: 'except' | 'notExcept' | '' | null,
    userId?: string | null,
    orderBy?: { value: string; direction: 'asc' | 'desc' },
    startAt?: any,
    limit?: any
  ) {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (this.shouldApplyActiveQueryDefault(collectionName)) {
          query = query.where('archived', '==', false);
        }
        if (limit) {
          query = query.limit(limit);
        }
        if (orderBy) {
          query = query.orderBy(orderBy.value, orderBy.direction);
        }
        if (startAt) {
          query = query.startAt(startAt[orderBy?.value ? orderBy.value : 'id']);
        }
        if (joinCode) {
          query = query.where('joinCode', '==', joinCode);
        }
        if (status) {
          query = query.where('status', except === 'except' ? '!=' : '==', status);
        }
        if (userId) {
          query = query.where('author.reference', '==', userId);
        }
        return query;
      })
      .valueChanges();
  }

  getSubjectByStudentId(studentId: string): Observable<any> {
    return this.afs
      .collection(collection.subjects, ref => {
        let query: CollectionReference | Query = ref;
        query = query.where('students', 'array-contains', studentId);
        query = query.where('archived', '==', false);
        return query;
      })
      .valueChanges();
  }

  getSubjectById(id: string, userId?: string | null): Observable<any> {
    return this.afs
      .collection(collection.subjects, ref => {
        let query: CollectionReference | Query = ref;
        query = query.where('id', '==', id);
        query = query.where('archived', '==', false);
        if (userId) {
          query = query.where('students', 'array-contains', userId);
        }
        return query;
      })
      .valueChanges();
  }

  async getQuizByIdNew(id: string): Promise<Quiz | null> {
    const quizSnapshot = await firstValueFrom(this.afs.collection(collection.quizzes).doc(id).get());
    if (quizSnapshot.exists) {
      const quiz = quizSnapshot.data() as Quiz;
      return (quiz as any)?.archived === true ? null : quiz;
    }
    return null;
  }

  addOrUpdateDocument(collectionName: string, data: any) {
    if (data.id) {
      return this.update(collectionName, data.id, data);
    } else {
      if (!data.id) {
        const id = this.afs.createId();
        data.id = id;
      }

      return this.afs
        .collection(collectionName, ref => ref)
        .doc(data.id)
        .set(this.applyActiveDefault(collectionName, data));
    }
  }

  async addOrUpdateDocumentWithReturnId(collectionName: string, data: any) {
    if (data.id) {
      await this.saveOrUpdateData(collectionName, data);
      return data.id;
    } else {
      if (!data.id) {
        const id = this.afs.createId();
        data.id = id;
      }
      await this.afs
        .collection(collectionName, ref => ref)
        .doc(data.id)
        .set(this.applyActiveDefault(collectionName, data));
    }
    return data.id;
  }

  getById(collectionName: string, id: string) {
    return this.afs.collection(collectionName).doc(id).valueChanges()
      .pipe(map(item => this.filterArchivedDocument(item)));
  }

  async getByIdOnce<T = any>(collectionName: string, id: string): Promise<T | null> {
    const snap = await this.afs.collection<T>(collectionName).doc(id).ref.get();
    if (!snap.exists) {
      return null;
    }
    const data = snap.data() as T;
    return (data as any)?.archived === true ? null : data;
  }

  getByMultiField(collectionName: string, limit = 24, startAt?: any, queryParam?: QueryParam[], orderBy?: any, noOrder = false) {
    return this.afs
      .collection(collectionName, ref => {
        let query: CollectionReference | Query = ref;
        if (this.shouldApplyActiveQueryDefault(collectionName, queryParam as any)) {
          query = query.where('archived', '==', false);
        }
        if (queryParam) {
          queryParam.forEach(e => {
            if (e.field && e.opStr && e.value !== undefined) {
              query = query.where(e.field, e.opStr as any, e.value);
            }
          });
        }
        if (limit) {
          query = query.limit(limit);
        }
        if (orderBy?.active && orderBy?.direction) {
          query = query.orderBy(orderBy.active, orderBy.direction);
        } else if (!noOrder) {
          query = query.orderBy('id');
        }
        if (startAt) {
          query = query.startAfter(startAt);
        }
        return query;
      })
      .valueChanges();
  }

  saveOrUpdateData(collection: string, data: any, partialUpdate: boolean = false): Promise<any> {
    return new Promise((resolve, reject) => {
      if (collection && data) {
        if (!data.id) {
          data.id = this.afs.createId();
        }
        if (!data.metadata) {
          data.metadata = {};
        }
        if (!data.metadata.createdBy) {
          data.metadata.createdBy = {};
        }
        if (!data.metadata.createdBy.id) {
          data.metadata.createdBy.id = this.logger.getUserId();
        }
        if (!data.metadata.createdAt) {
          data.metadata.createdAt = new Date().toISOString();
        }
        if (!data.metadata.lastUpdatedBy) {
          data.metadata.lastUpdatedBy = {};
        }
        // Always update lastUpdated at and by
        data.metadata.lastUpdatedAt = new Date().toISOString();
        data.metadata.lastUpdatedBy.id = this.logger.getUserId();
        this.applyActiveDefault(collection, data, partialUpdate);
        this.afs
          .collection(collection)
          .doc(data.id)
          .set(data, { merge: partialUpdate ? true : false })
          .then(() => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        reject();
      }
    });
  }

  /** @deprecated Use 'saveOrUpdateData(collection: string, data: any)' instead */
  update(collectionName: string, id: string, data: any) {
    return this.afs.collection(collectionName).doc(id).update(data);
  }

  delete(collectionName: string, id: string) {
    return this.afs.collection(collectionName).doc(id).update(this.buildArchiveUpdate());
  }
}
